import React, { useCallback, useEffect, useRef, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import {
    Autocomplete,
    Box,
    Button,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCustomerData } from '../../redux/slices/customerSlice';
import { fetchRepsData } from '../../redux/slices/repSlice';
import { fetchProductData } from '../../redux/slices/productSlice';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { apiRequest } from '../../utils/api';
import DeleteIcon from '@mui/icons-material/Delete';
import '../../print/PrintStyle.css';

export default function CreateOrder() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const fieldProductRef = useRef(null);

    const { data: products, isLoading: isLoadingProducts, error: errorProducts } = useSelector((state) => state.product);
    const { data: customers, isLoading: isLoadingCustomers, error: errorCustomers } = useSelector((state) => state.customer);
    const { data: reps, isLoading: isLoadingReps, error: errorReps } = useSelector((state) => state.rep);

    const [selectedRep, setSelectedRep] = React.useState(null);
    const [selectedCustomer, setSelectedCustomer] = React.useState(null);
    const [selectedStatus, setSelectedStatus] = React.useState(null);
    const [comments, setComments] = React.useState('');

    const [selectedProduct, setSelectedProduct] = useState({});
    const [qtyOrder, setqtyOrder] = useState(0);
    const [qtyDispatch, setQtyDispatch] = useState(0);

    const [productList, setProductList] = React.useState([]);

    const [loadReq, setLoadReq] = useState(false);

    useEffect(() => {
        if (!customers) {
            dispatch(fetchCustomerData());
        }
        if (!reps) {
            dispatch(fetchRepsData());
        }
        if (!products) {
            dispatch(fetchProductData());
        }

        return () => {
            setSelectedCustomer(null);
            setSelectedRep(null);
            setSelectedStatus(null);
        };
    }, []);

    const handleStatus = useCallback((e) => {
        setSelectedStatus(e?.target?.value);
    }, []);

    const handleCreate = async () => {
        setLoadReq(true);
        try {
            if (selectedRep == null) {
                return toast.error('Please select rep');
            }
            if (selectedCustomer == null) {
                return toast.error('Please select customer');
            }
            if (selectedStatus == null) {
                return toast.error('Please select status');
            }

            let filterProductList = productList?.filter((i) => i?.qtyOrder !== 0);
            if (filterProductList?.length == 0) {
                return toast.error('Please add atleast one product');
            }

            let data = {
                user: selectedRep,
                customer: selectedCustomer,
                comments: comments,
                orderItems: filterProductList,
                status: selectedStatus
            };

            const res = await apiRequest('post', 'orders', data);

            if (res?.data) {
                navigate(-1);
                return toast.success('Order created successfully');
            }
        } catch (error) {
            return toast.error(error?.response?.data?.message ?? error?.response?.data ?? error?.message);
        } finally {
            setLoadReq(false);
        }
    };

    const handleAddProduct = () => {
        if (selectedProduct?.id == null) {
            return toast.error('Please select any product to add');
        }
        if (qtyOrder < 1) {
            return toast.error('Quantity Order should be atleast 1');
        }

        let newProduct = {
            productName: selectedProduct?.name,
            id: selectedProduct?.id,
            qtyDispatch,
            qtyOrder,
            delId: productList?.length + 1
        };

        setProductList([...productList, newProduct]);
        setSelectedProduct({});
        setqtyOrder(1);
        setQtyDispatch(0);
        fieldProductRef.current.focus();
    };

    return (
        <Layout>
            <Grid container sx={{ mb: 2 }} spacing={2} alignItems={'center'}>
                <Grid item lg={4} sm={3} md={6.5} xs={6}>
                    <Typography variant="h6">Create Order</Typography>
                </Grid>

                <Grid item lg={2.5} sm={5} md={3} xs={12}>
                    <Autocomplete
                        size="small"
                        options={reps ?? []}
                        getOptionLabel={(rep) => `${rep?.firstName} ${rep?.lastName} - ${rep?.phoneNo}`}
                        value={reps?.find((i) => i?.id == selectedRep)}
                        onChange={(event, selected) => {
                            setSelectedRep(selected?.id || null);
                        }}
                        renderInput={(params) => <TextField {...params} label="Rep" variant="outlined" />}
                    />
                </Grid>

                <Grid item lg={2.5} sm={5} md={3} xs={12}>
                    <Autocomplete
                        size="small"
                        options={customers?.filter((i) => i?.user?._id == selectedRep) ?? []}
                        getOptionLabel={(customer) => `${customer?.name} - ${customer?.phoneNo}`}
                        value={customers?.find((i) => i?.id == selectedCustomer)}
                        onChange={(event, selected) => {
                            setSelectedCustomer(selected?.id || null);
                        }}
                        renderInput={(params) => <TextField {...params} label="Customer" variant="outlined" />}
                    />
                </Grid>

                <Grid item lg={1.5} sm={5} md={3} xs={12}>
                    <FormControl size="small" fullWidth>
                        <InputLabel id="demo-simple-select-label">Status</InputLabel>
                        <Select
                            labelId="demo-simple-select-label"
                            id="demo-simple-select"
                            value={selectedStatus}
                            label="Status"
                            onChange={(e) => handleStatus(e)}
                        >
                            <MenuItem value={'Order Placed'}>Order Placed</MenuItem>
                            <MenuItem value={'In Process'}>In Process</MenuItem>
                            <MenuItem value={'Hold'}>Hold</MenuItem>
                            <MenuItem value={'Order Approved'}>Order Approved</MenuItem>
                            <MenuItem value={'Ready for Dispatch'}>Ready for Dispatch</MenuItem>
                            <MenuItem value={'Dispatched'}>Dispatched</MenuItem>
                            <MenuItem value={'Cancelled'}>Cancelled</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item lg={1.5} sm={3.5} md={2.5} xs={12} sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Button disabled={loadReq} onClick={() => handleCreate()} fullWidth variant="contained">
                        Create Order
                    </Button>
                </Grid>
            </Grid>

            <Grid container spacing={2}>
                <Grid item lg={3} sm={5} md={3} xs={12}>
                    <Autocomplete
                        size="small"
                        options={products ?? []}
                        getOptionLabel={(prod) => `${prod?.name} - ${prod?.packing}`}
                        value={products?.find((i) => i?.id == selectedProduct?.id) || null}
                        onChange={(event, selected) => {
                            setSelectedProduct(selected);
                        }}
                        renderInput={(params) => <TextField inputRef={fieldProductRef} {...params} label="Product" variant="outlined" />}
                    />
                </Grid>

                <Grid item lg={1} sm={5} md={3} xs={4}>
                    <TextField
                        size="small"
                        // type="number"
                        value={qtyOrder}
                        onChange={(e) => setqtyOrder(e?.target?.value)}
                        fullWidth
                        label={'Quantity Order'}
                    />
                </Grid>

                <Grid item lg={1} sm={5} md={3} xs={4}>
                    <TextField
                        size="small"
                        // type="number"
                        value={qtyDispatch}
                        onChange={(e) => setQtyDispatch(e?.target?.value)} // Math.max(0, Math.min(qtyOrder, parseInt(e?.target?.value) || 0))
                        fullWidth
                        label={'Quantity Dispatch'}
                    />
                </Grid>

                <Grid item lg={1.5} sm={5} md={3} xs={4}>
                    <Button onClick={handleAddProduct} variant="contained" fullWidth>
                        Add
                    </Button>
                </Grid>

                <Grid item lg={5.5} sm={5} md={3} xs={12}>
                    <TextField fullWidth size="small" label="Comments" value={comments} onChange={(e) => setComments(e?.target?.value)} />
                </Grid>

                <Grid onClick={() => handleCreate()} item lg={1.5} sm={3.5} md={2.5} xs={12} sx={{ display: { xs: 'block', sm: 'none' } }}>
                    <Button fullWidth variant="contained">
                        Create Order
                    </Button>
                </Grid>
            </Grid>

            <TableContainer component={Paper} sx={{ marginTop: 2 }}>
                <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
                    <TableHead>
                        <TableRow>
                            <TableCell>S. No.</TableCell>
                            <TableCell>Product Name</TableCell>
                            <TableCell>Quantity Order</TableCell>
                            <TableCell>Quantity Dispatch</TableCell>
                            <TableCell sx={{ width: '60px' }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {productList?.map((row, index) => (
                            <TableRow
                                key={row.id + index}
                                sx={{
                                    '&:last-child td, &:last-child th': { border: 0 }
                                }}
                            >
                                <TableCell component="th" scope="row">
                                    {index + 1}
                                </TableCell>
                                <TableCell component="th" scope="row">
                                    {row?.productName}
                                </TableCell>
                                <TableCell>{row?.qtyOrder}</TableCell>
                                <TableCell>{row?.qtyDispatch}</TableCell>
                                <TableCell>
                                    <IconButton
                                        onClick={() => {
                                            setProductList(productList?.filter((i) => i?.delId !== row?.delId));
                                        }}
                                        aria-label="Delete"
                                        size="small"
                                    >
                                        <DeleteIcon sx={{ color: 'red' }} />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Layout>
    );
}
